import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/Accordion";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

/**
 * `label` is the accordion's title and comes from the server's `category_label`.
 * It is not derived from `category` here: "Language & Proficiency" is a title no
 * key title-cases into, and a second copy of the wording on the client is a copy
 * that drifts. `category` stays the accordion's identity only.
 *
 * `derivedKeys` are the fields the JOB decides, not the recruiter — the server
 * recomputes them on every write, so a tick here would be overwritten on save.
 * They render read-only under `derivedNote`, which says what does decide them.
 * A group with nothing left to choose loses its bulk buttons too: Select All
 * and Clear over a fully derived group are two controls that do nothing.
 */
const CategorySection = ({
  category = "",
  label = "Other",
  fields = [],
  mandatoryFieldSet = new Set(),
  derivedKeys = new Set(),
  derivedNote = "",
  onToggle,
  onBulk,
  disabled = false,
}) => {
  const selectableKeys = fields.filter((f) => !derivedKeys.has(f.key)).map((f) => f.key);
  const hasDerived = fields.some((f) => derivedKeys.has(f.key));

  return (
    <AccordionItem value={category}>
      <AccordionTrigger className="py-2">
        <span className="text-sm font-medium">{label}</span>
      </AccordionTrigger>
      <AccordionContent className="pb-3">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 bg-background-form/30 rounded-lg p-3">
          {fields.map((field) => {
            const isMandatory = mandatoryFieldSet.has(field.key);
            const isDerived = derivedKeys.has(field.key);
            const checkboxId = `field-checkbox-${field.key}`;

            return (
              <div key={field.key} className="flex items-center gap-3 py-1.5">
                <Input
                  id={checkboxId}
                  type="checkbox"
                  disabled={disabled || isDerived}
                  checked={isMandatory}
                  onChange={() => onToggle(field.key)}
                  className={
                    isDerived
                      ? "h-4 w-4 rounded border-border-primary bg-background-form text-red-400 cursor-not-allowed"
                      : "h-4 w-4 rounded border-border-primary bg-background-form text-red-400 focus:ring-red-400 cursor-pointer"
                  }
                />
                <label
                  htmlFor={checkboxId}
                  className={isDerived ? "text-sm text-text-primary cursor-not-allowed" : "text-sm text-text-primary cursor-pointer"}
                >
                  {field.label}
                </label>
                {isMandatory && <span className="ml-auto text-xs text-text-primary bg-background-invert/15 px-2 py-0.5 rounded">Mandatory</span>}
              </div>
            );
          })}
        </div>
        {hasDerived && derivedNote && <p className="mt-2 text-[12px] text-text-secondary">{derivedNote}</p>}
        {selectableKeys.length > 0 && (
          <div className="flex gap-2 mt-3">
            <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7 text-text-primary" onClick={() => onBulk(selectableKeys, true)}>
              Select All
            </Button>
            <Button disabled={disabled} type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => onBulk(selectableKeys, false)}>
              Clear
            </Button>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export default CategorySection;
